// entity-validator.ts
import { Change, nothingHere } from "@adornis/base/change";
import { constructValue } from "@adornis/baseql/entities/adornisEntity";
var EntityValidator = class {
  static fromEntity(entity, options) {
    return new EntityValidator(entity, options);
  }
  constructor(entity, options) {
    if (options) {
      if (options.whitelist && options.blacklist)
        throw new Error("You cannot use whitelist and blacklist at the same time");
      this.options = options;
    }
    this.entityClass = entity;
  }
  get fields() {
    return this.entityClass.fields;
  }
  construct(doc) {
    doc._class = this.entityClass._class;
    return constructValue(doc);
  }
  /**
   *
   * @param doc takes an object (EntityInitializer) to validate
   * @param options takes an options object
   *   - whitelist - takes an array of field names to check (cannot be used in combination with blacklist)
   *   - blacklist - takes an array of field name **not** to check (cannot be use in combination with whitelist)
   *
   * @returns a constructed entity if possible, else the validated object
   * @throws ValidationError - if fields to validate throw a ValidationError
   */
  validate(doc) {
    doc._class = this.entityClass._class;
    for (const field of this.fields.filter((field2) => {
      if (!this.options)
        return true;
      if (this.options.whitelist)
        return this.options.whitelist.includes(field2.name);
      if (this.options.blacklist)
        return !this.options.blacklist.includes(field2.name);
      return true;
    })) {
      const validator = this.entityClass.prototype.validator;
      validator?.functions[field.name]?.(doc[field.name], field.name, doc);
    }
    if (!this.options?.whitelist && !this.options?.blacklist) {
      const entity = constructValue(doc);
      entity.validate();
      return entity;
    }
    try {
      return constructValue(doc);
    } catch {
      return doc;
    }
  }
  validateField(doc, fieldName) {
    const resolvedValue = Change.resolvePath(doc, Array.isArray(fieldName) ? fieldName : [fieldName]);
    this.validateFieldValue(resolvedValue === nothingHere ? void 0 : resolvedValue, doc, fieldName);
    return true;
  }
  validateFieldValue(value, doc, fieldName) {
    const field = findField(this.fields, Array.isArray(fieldName) ? fieldName : [fieldName]);
    const entityClass = Array.isArray(fieldName) && fieldName.length > 1 ? findField(
      this.fields,
      typeof fieldName[fieldName.length - 2] === "number" ? fieldName.slice(0, -2) : fieldName.slice(0, -1)
    ).type?.() || this.entityClass : this.entityClass;
    const validator = entityClass.prototype.validator;
    const validationFunction = validator?.functions[field.name];
    const resolvedDoc = Array.isArray(fieldName) ? Change.resolvePath(doc, fieldName.slice(0, -1)) : doc;
    validationFunction?.(
      value,
      Array.isArray(fieldName) ? fieldName.slice(-1)[0] : fieldName,
      resolvedDoc
    );
    return true;
  }
};
var findField = (currFields, fieldNames) => {
  const field = currFields.find((field2) => field2.name === fieldNames[0]);
  if (field && (fieldNames.length === 1 || fieldNames.length - field.arrayDepth() === 1))
    return field;
  if (field && fieldNames.length > 1) {
    const newClass = field.type?.();
    if (!newClass || !newClass?.fields) {
      throw new Error("fieldNames contains fields that cannot be iterated");
    }
    return findField(newClass.fields, fieldNames.slice(1 + field.arrayDepth()));
  }
  throw new Error("fieldNames contains fields that cannot be iterated");
};
export {
  EntityValidator
};
